import { useEffect, useRef } from 'react'
import { roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'
import NameValueTable from '../../ui-components/NameValueTable'
import NameValueTableRow from '../../ui-components/NameValueTableRow'
import BottomSheetContent from '../BottomSheetContent'
import { STCG } from './STCG'
import { LTCG } from './LTCG'
import { type TaxLiabilityData, isELSSFund, getRedeemableValue, getTaxDescriptionText, getInfoText } from './utils'
import { BottomDescription } from './BottomDescription'
import { MetaDataResponse, TaxCategories } from '../../../types/mutual-fund-detail.types'
import { TAX_LIABILITY_BREAKUP_HEADING_ID } from './constants'

/**
 * Gets the value table data based on fund type (ELSS or non-ELSS)
 */
const getValueTableData = (taxLiabilityData: TaxLiabilityData) => {
  const isELSS = isELSSFund(taxLiabilityData?.fundType)
  const redeemableValue = getRedeemableValue(taxLiabilityData)

  if (isELSS) {
    return [
      {
        name: 'Current Value',
        value: `${roundOffAndFormatNumberWithCurrency(taxLiabilityData?.currentValue)}`,
        nameClassName: '!text-[#3D3D3D] !text-[13px]'
      },
      {
        name: 'Redeemable Value',
        value: `${roundOffAndFormatNumberWithCurrency(redeemableValue)}`,
        nameClassName: '!text-[#3D3D3D] !text-[13px]'
      }
    ]
  }

  return [
    {
      name: 'Current Value',
      value: `${roundOffAndFormatNumberWithCurrency(taxLiabilityData?.currentValue)}`,
      nameClassName: '!text-[#3D3D3D] !text-[13px]'
    },
    {
      name: 'Exit Load',
      value: `${roundOffAndFormatNumberWithCurrency(taxLiabilityData?.exitLoad)}`,
      nameClassName: '!text-[#3D3D3D] !text-[13px]'
    },
    {
      name: 'Pre-Tax Value',
      value: `${roundOffAndFormatNumberWithCurrency(taxLiabilityData?.preTaxValue ?? 0)}`,
      nameClassName: '!text-[#3D3D3D] !text-[13px]'
    }
  ]
}

export const TaxLiabilityL2BreakupBottomSheet = ({
  taxLiabilityData,
  fundCategory,
  heading,
  onDismiss
}: {
  taxLiabilityData: TaxLiabilityData
  fundCategory: MetaDataResponse['fundDetails']['category'] | undefined
  heading: string
  onDismiss: () => void
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null)
  const taxDescription = getTaxDescriptionText(taxLiabilityData)
  const infoText = getInfoText(taxLiabilityData?.fundType)
  const valueTableData = getValueTableData(taxLiabilityData).map((row) => {
    if (row.name === 'Pre-Tax Value' && infoText && !isELSSFund(taxLiabilityData?.fundType)) {
      return {
        ...row,
        accessibilityLabel: `Pre-Tax Value, ${row.value}, ${infoText}`
      }
    }
    if (row.name === 'Redeemable Value' && infoText && isELSSFund(taxLiabilityData?.fundType)) {
      return {
        ...row,
        accessibilityLabel: `Redeemable Value, ${row.value}, ${infoText}`
      }
    }
    return row
  })

  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  return (
    <BottomSheetContent
      headingComponent={
        <div className='flex flex-col max-w-[70%] px-5'>
          <h2
            id={TAX_LIABILITY_BREAKUP_HEADING_ID}
            ref={headingRef}
            tabIndex={-1}
            className='text-[#232323] text-[22px] font-[500] outline-none'
          >
            {heading}
          </h2>
          <div className='text-[#6A6E6E] text-[13px] font-[400] pt-1 pl-[1px]'>
            <span className='text-black'>
              {roundOffAndFormatNumberWithCurrency(taxLiabilityData?.taxLiability ?? 0)}
            </span>{' '}
            estimated tax {taxDescription}
          </div>
        </div>
      }
      bottomSheetHeadClassName='border-b border-[#F0F4F7] px-0 pb-5'
      headingTitleId={TAX_LIABILITY_BREAKUP_HEADING_ID}
      onDismiss={onDismiss}
    >
      <div className='flex flex-col w- pt-4'>
        <div className='w-full pt-1'>
          <NameValueTable data={valueTableData} gap={12} groupForAccessibility />
        </div>
        <div aria-hidden='true' className='text-[#6A6E6E] text-[13px] font-[400] pt-[3px] pb-6'>
          {infoText}
        </div>
        <hr aria-hidden='true' />
        <div className='pt-4 w-full flex flex-col'>
          {taxLiabilityData?.stcgTaxInfo?.investedAmount > 0 && <STCG taxLiabilityData={taxLiabilityData} />}
          {taxLiabilityData?.ltcgTaxInfo?.investedAmount > 0 && <LTCG taxLiabilityData={taxLiabilityData} />}
        </div>
        <div className='pb-6 w-full'>
          <NameValueTableRow
            name='Tax Liability'
            value={`${roundOffAndFormatNumberWithCurrency(taxLiabilityData?.taxLiability ?? 0)}`}
            nameClassName='!text-[#232323] !font-[500]'
            valueClassName='!text-[22px]'
            groupForAccessibility
          />
        </div>
        <hr aria-hidden='true' />
        <div className='w-full pt-4 pb-5'>
          <NameValueTableRow
            name='Post-Tax Value'
            value={`${roundOffAndFormatNumberWithCurrency(taxLiabilityData?.postTaxValue ?? 0)}`}
            nameClassName='!text-[#3D3D3D] !text-[13px]'
            groupForAccessibility
          />
        </div>
        <hr aria-hidden='true' />
        <BottomDescription
          taxCategory={taxLiabilityData?.taxCategory as `${TaxCategories}`}
          fundCategory={fundCategory ?? 'Such'}
        />
      </div>
    </BottomSheetContent>
  )
}
